import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { Callout, H2, H3, OL, P, Pull, UL } from './ui';

const post: Post = { meta: postMeta('how-to-host-a-beat-battle'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        Running your own beat battle is more work than entering one. You are responsible for the
        rules, the kit, the timing, the voting process, and making sure nobody cheats. Done right,
        it builds community fast. Done badly, it collapses in arguments over whether someone's beat
        was pre-made.
      </P>
      <P>
        This guide covers two paths: hosting on a dedicated platform (the easy route) and hosting
        independently through Discord or a forum (the harder route with more control). Read both
        before you decide which fits your situation.
      </P>

      <H2 id="before-you-start">Before you start: what kind of beat battle are you running?</H2>
      <P>Answer these four questions first. The answers determine most of your other decisions.</P>
      <OL>
        <li>
          <strong>Real-time or async?</strong> Real-time matches run 15 to 30 minutes live. Async
          gives producers 24 hours to a week. Real-time is harder to schedule but more exciting.
          Async is easier to run but requires chasing people for submissions.
        </li>
        <li>
          <strong>Shared kit or open samples?</strong> Shared kit (same drums, melodies, one-shots
          for every producer) levels the playing field and makes results more comparable. Open
          samples allow more creativity but make judging harder because the starting points are
          different.
        </li>
        <li>
          <strong>Blind or named voting?</strong> Blind voting (no producer names shown during the
          vote) keeps it about the beat. Named voting is easier to run but invites popularity
          contests. Always use blind voting if you can.
        </li>
        <li>
          <strong>How many producers?</strong> 2-to-1 (head-to-head) is the easiest to manage.
          4-to-8 is the most common FFA format. More than 8 in a single match is hard to judge
          because listener fatigue kicks in before the last entries.
        </li>
      </OL>

      <H2 id="hosting-on-platform">Option 1: Host on a dedicated platform</H2>
      <P>
        The simplest way to run a beat battle in 2026 is to use a platform that handles the
        infrastructure for you. On{' '}
        <Link to="/" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>
        , you can open a private room and invite up to 5 producers for a 1v1 through 5v5 match. The
        platform handles the kit drop, the timer, the submission system, and the blind voting. You
        just set the genre and send the room link.
      </P>
      <H3>How to open a private room on Prod Battle</H3>
      <OL>
        <li>
          Log in and go to{' '}
          <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
            Play
          </Link>
          .
        </li>
        <li>Choose "Private Room" from the mode selector.</li>
        <li>Set the genre, BPM range, and round length.</li>
        <li>Copy the room code and send it to the producers you are inviting.</li>
        <li>Once everyone has joined, start the match. The platform takes it from there.</li>
      </OL>
      <P>
        This approach is best for casual battles between friends, communities running their own
        weekly events, or Discord servers that want to run bracket tournaments without building any
        infrastructure.
      </P>
      <Callout>
        Private rooms on Prod Battle do not affect ranked ratings by default. Good for tournaments
        where you want a clean bracket separate from the main leaderboard.
      </Callout>

      <H2 id="hosting-independently">Option 2: Host independently (Discord or forum)</H2>
      <P>
        If you want full control over the format, want to run something on a platform that does not
        exist yet, or need to host a large tournament with custom rules, you will need to build the
        process yourself. Here is the full breakdown.
      </P>

      <H3>Step 1: Write the rules first</H3>
      <P>
        Most beat battles fail because the rules were not clear before the match started. Write your
        rules as a pinned message before you open registration. Cover at minimum:
      </P>
      <UL>
        <li>
          <strong>Genre and BPM range.</strong> Be specific. "Trap" is too broad. "Trap, 140-160
          BPM, 808 required" is clear enough to judge against.
        </li>
        <li>
          <strong>Kit rules.</strong> Can producers add their own sounds on top of the shared kit,
          or is the kit locked? If locked, be explicit.
        </li>
        <li>
          <strong>Time limit.</strong> When does the timer start and when does submission close?
          Specify the timezone.
        </li>
        <li>
          <strong>Beat length.</strong> Minimum and maximum length for submissions. 45 seconds to 2
          minutes covers most formats.
        </li>
        <li>
          <strong>Format.</strong> WAV or MP3? Sample rate? Will you do loudness normalization
          before voting?
        </li>
        <li>
          <strong>Pre-made beats.</strong> Are pre-made beats allowed, or does the beat have to be
          made during the round? This is the most contested rule. Be clear.
        </li>
        <li>
          <strong>Voting process.</strong> Who votes, how, and on what scale.
        </li>
        <li>
          <strong>Tiebreakers.</strong> What happens if two entries tie on score.
        </li>
      </UL>

      <H3>Step 2: Build and distribute the sample kit</H3>
      <P>
        If you are running a shared-kit battle, the kit is the most important thing you produce
        before the match. A bad kit makes for bad beats regardless of who is competing. Guidelines:
      </P>
      <UL>
        <li>
          Include enough sounds to make a complete beat: at minimum a kick, snare, hi-hat, and one
          melodic element. Add a sub bass or 808 if the genre expects it.
        </li>
        <li>
          Keep the kit small. 20 to 40 samples is the right range. More than that and producers
          spend half the session auditioning sounds instead of building.
        </li>
        <li>
          Normalize all samples to the same peak loudness before distributing. If one kick is 10 dB
          louder than another, producers who pick that kick have an unfair advantage in perceived
          loudness.
        </li>
        <li>
          Distribute the kit in a .zip file at exactly the moment the match starts, not before.
          Pre-distributing kits means producers can pre-make beats.
        </li>
        <li>
          Use a consistent folder structure: Drums, Melodies, FX, Basses. Makes the DAW import
          faster.
        </li>
      </UL>

      <H3>Step 3: Run the timer</H3>
      <P>
        For async battles, post the kit and a clear submission deadline in your Discord or forum
        thread. Use a third-party timer bot (many Discord servers use Sesh or Event Scheduler) so
        the countdown is visible to everyone.
      </P>
      <P>
        For real-time battles, you need everyone in the same voice channel or thread at the same
        time. Drop the kit file link, start a shared timer (the browser app Timer.guru works fine
        and is free), and announce when submission is open.
      </P>
      <Callout>
        The most common real-time battle failure: the host forgets that not everyone's download
        speed is the same. Give a 2-minute download window after dropping the kit before the
        production timer starts.
      </Callout>

      <H3>Step 4: Collect submissions</H3>
      <P>For independent battles, you need a submission channel or DM system. Options:</P>
      <UL>
        <li>
          <strong>Discord bot (Carl-bot, Ticket Tool).</strong> Create a private channel per
          producer or a ticket system where each producer submits their file to a hidden thread
          visible only to you. This prevents producers from hearing others' beats before voting.
        </li>
        <li>
          <strong>Google Form with file upload.</strong> Set up a Google Form with a file upload
          field. Submissions go to Google Drive. Shares automatically with anyone with the form
          link. Easy to set up, free.
        </li>
        <li>
          <strong>Direct DM to host.</strong> Simplest but does not scale. Works fine for battles
          with 4 or fewer producers.
        </li>
      </UL>
      <P>
        Always acknowledge each submission as it comes in. Producers need to know their file was
        received before voting starts.
      </P>

      <H3>Step 5: Run blind voting</H3>
      <P>This is the part most independently-run battles mess up. Common mistakes:</P>
      <UL>
        <li>
          Posting beats with producer names attached. Strips the blind from blind voting
          immediately. Rename files to Beat_1, Beat_2, Beat_3 before posting for voting.
        </li>
        <li>
          Letting producers vote on their own match. Block this manually or use a Google Form that
          checks submission email against voter email.
        </li>
        <li>
          Using a simple "react with emoji" voting system. Easy to manipulate, hard to audit. Use a
          Google Form or a Discord bot with proper vote tracking instead.
        </li>
      </UL>
      <P>
        For a clean DIY voting system: post the beats (renamed) as unlisted SoundCloud uploads or a
        private Dropbox folder. Send voters a Google Form with a 1-to-5 rating field per entry.
        Aggregate the results when the form closes.
      </P>

      <H3>Step 6: Announce results</H3>
      <P>
        Post the full results: scores for every entry, not just the winner. Producers who lose
        deserve to know their score relative to the field. Anonymize the vote breakdown if you are
        worried about conflict (showing who scored what is fine in tight communities, less fine in
        larger public battles).
      </P>
      <P>
        Reveal producer names alongside scores. If you ran it blind, this is the moment that makes
        the reveal fun.
      </P>

      <H2 id="running-a-tournament">Running a tournament bracket</H2>
      <P>
        Single-elimination is the easiest to manage. Seed producers by prior performance if you have
        data, or randomly if this is a first-time event. Run one match per round. Winners advance.
        Standard format for a 8-producer tournament:
      </P>
      <OL>
        <li>Round 1: 4 matches (8 producers become 4 winners)</li>
        <li>Semifinal: 2 matches (4 producers become 2 winners)</li>
        <li>Final: 1 match (2 producers, one champion)</li>
      </OL>
      <P>
        Double-elimination (losers get a second chance through a losers bracket) is fairer but
        roughly doubles the number of matches and the time to complete. Only do this if you have the
        bandwidth to run it consistently.
      </P>
      <P>
        For weekly tournaments across multiple genres, the built-in{' '}
        <Link to="/tournaments" className="underline decoration-primary/50 underline-offset-2">
          tournament system on Prod Battle
        </Link>{' '}
        handles bracket management automatically. Worth using that rather than building it yourself
        if the platform's genre list covers your event.
      </P>

      <H2 id="common-problems">Common problems and how to handle them</H2>
      <H3>Producer submits late</H3>
      <P>
        Do not accept late submissions. Ever. The rule only works if it is enforced every time. An
        "okay this one time" sets a precedent that the deadline is negotiable and creates resentment
        from producers who submitted on time.
      </P>
      <H3>Someone claims another producer pre-made their beat</H3>
      <P>
        Unless you have proof (a SoundCloud upload with an earlier date, a stream timestamp), you
        cannot act on this. The most you can do is note the claim in your moderation log and apply
        more scrutiny to that producer's next submission. Publicly accusing producers of cheating
        without evidence kills community trust fast.
      </P>
      <H3>Voting manipulation (friends voting for each other)</H3>
      <P>
        The best prevention is having more voters than competitors. If you have 6 producers in a
        match and 60 community members voting, the impact of 6 producers each voting for their
        friend is noise. If you have 6 producers and 8 voters, it is not a beat battle, it is a
        popularity contest.
      </P>

      <Pull>
        The vote quality of a beat battle depends almost entirely on the ratio of voters to
        producers. Grow your voter audience first.
      </Pull>

      <H2 id="rules-checklist">Pre-battle checklist</H2>
      <UL>
        <li>Rules published at least 24 hours before the match</li>
        <li>Sample kit tested in at least one DAW before distributing</li>
        <li>Submission system tested with a dummy file</li>
        <li>Voting system tested with a dummy vote</li>
        <li>Timezone and deadline confirmed in at least 2 formats (time + countdown)</li>
        <li>At least one backup host or moderator if you cannot be present</li>
      </UL>

      <H2 id="see-it-in-practice">See it in practice</H2>
      <P>
        The easiest way to understand how a well-run beat battle works is to enter a few first. Play
        on{' '}
        <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>{' '}
        in quickplay mode before you run your own event. Watch how the kit drop, timing, submission,
        and blind voting actually feel from the producer side. The better hosts are almost always
        producers who have competed in a lot of matches first.
      </P>
      <P>
        Check the{' '}
        <a href="/faq#packs" className="underline decoration-primary/50 underline-offset-2">
          FAQ on how sample packs work
        </a>{' '}
        for more detail on how kits are constructed and distributed on the platform.
      </P>
    </>
  );
}
